import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import SPBackend from '../../services/SPBackend';
import { log } from '../../utils/jsUtils';

// Default tenantsPageInfo values
const defaultTenantsPageInfo = {
  pageNum: 1,
  pageSize: 100,
  sortOrder: 1,
  sortBy: ['FirstName', 'LastName']
};

export const tenantsApi = createApi({
  reducerPath: 'tenantsApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/' }), // Using SPBackend directly
  tagTypes: ['Tenants'],
  endpoints: (builder) => ({
    // Fetch tenants with search and pagination
    getTenants: builder.query({
      queryFn: async ({ searchVal = '', tenantsPageInfo = {}, user }) => {
        try {
          // Merge the default values with any provided pageInfo
          const mergedTenantsPageInfo = {
            ...defaultTenantsPageInfo,
            ...tenantsPageInfo
          };
          
          log('[tenantsApi.getTenants] Fetching tenants', { 
            searchVal, 
            tenantsPageInfo: mergedTenantsPageInfo,
            timestamp: new Date().toISOString()
          });
          
          const findTenantsPayload = {
            search: searchVal,
            tenantsPageInfo: mergedTenantsPageInfo
          };
          
          const response = await SPBackend.findTenants(findTenantsPayload, user);
          
          const { tenants, tenantsNextPage, tenantsCount } = response.data;
          
          return { 
            data: {
              tenants,
              tenantsNextPage,
              tenantsCount
            }
          };
        } catch (error) {
          log('[tenantsApi] Error fetching tenants:', error);
          return { 
            error: {
              status: error.status || 'FETCH_ERROR',
              data: error.response ? error.response.data : error.message || 'Failed to fetch tenants'
            } 
          };
        }
      },
      providesTags: ['Tenants']
    }),

    // ==================== Customer Import ====================

    // Get presigned URL for S3 upload
    getImportPresign: builder.mutation({
      queryFn: async ({ fileName, user }) => {
        try {
          log('[tenantsApi.getImportPresign] Getting presigned URL', { fileName });
          const response = await SPBackend.getImportPresignedUrl(fileName, user);
          return { data: response.data };
        } catch (error) {
          log('[tenantsApi.getImportPresign] Error:', error);
          return {
            error: {
              status: error.response?.status || 'FETCH_ERROR',
              data: error.response?.data || error.message || 'Failed to get presigned URL'
            }
          };
        }
      }
    }),

    // Start import (parse uploaded CSV)
    startImport: builder.mutation({
      queryFn: async ({ jobId, user }) => {
        try {
          log('[tenantsApi.startImport] Starting import', { jobId });
          const response = await SPBackend.startTenantImport(jobId, user);
          return { data: response.data };
        } catch (error) {
          log('[tenantsApi.startImport] Error:', error);
          return {
            error: {
              status: error.response?.status || 'FETCH_ERROR',
              data: error.response?.data || error.message || 'Failed to start import'
            }
          };
        }
      }
    }),

    // Process import with column mapping
    processImport: builder.mutation({
      queryFn: async ({ jobId, columnMapping, user }) => {
        try {
          log('[tenantsApi.processImport] Processing import', { jobId, columnMapping });
          const response = await SPBackend.processTenantImport(jobId, columnMapping, user);
          return { data: response.data };
        } catch (error) {
          log('[tenantsApi.processImport] Error:', error);
          return {
            error: {
              status: error.response?.status || 'FETCH_ERROR',
              data: error.response?.data || error.message || 'Failed to process import'
            }
          };
        }
      },
      // Invalidate tenants cache when import starts processing
      // (will be refreshed again when complete via manual invalidation)
    }),

    // Get import status (for polling)
    getImportStatus: builder.query({
      queryFn: async ({ jobId, user }) => {
        try {
          log('[tenantsApi.getImportStatus] Getting status', { jobId });
          const response = await SPBackend.getTenantImportStatus(jobId, user);
          return { data: response.data };
        } catch (error) {
          log('[tenantsApi.getImportStatus] Error:', error);
          return {
            error: {
              status: error.response?.status || 'FETCH_ERROR',
              data: error.response?.data || error.message || 'Failed to get import status'
            }
          };
        }
      }
    })
  })
});

export const {
  useGetTenantsQuery,
  useGetImportPresignMutation,
  useStartImportMutation,
  useProcessImportMutation,
  useGetImportStatusQuery
} = tenantsApi; 